import { Edit2, Plus, Save, Trash2 } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import { useEffect, useMemo, useState } from 'react';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS } from '../components/common/filters';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import useServerTable from '../hooks/useServerTable';
import componentsService from '../services/components';
import useRoles from '../hooks/useRoles';
import { splitRoles } from '../utils/roleFlags';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { Field } from '../components/ui/Field';
import { TextInput, Textarea } from '../components/ui/Input';

const ComponentsPage = () => {
    const { loading: loadingApps, error: appsError, selectedAppId: appId } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showComponentRolesModal, setShowComponentRolesModal] = useState(false);

    // Selection
    const [selectedComponent, setSelectedComponent] = useState(null);

    // Role Mapping State
    const [componentRoles, setComponentRoles] = useState([]);
    const [loadingComponentRoles, setLoadingComponentRoles] = useState(false);
    const [mappingError, setMappingError] = useState('');

    // Roles offered for mapping; refetched each time the mapping modal opens.
    // Flag roles grant eligibility only — never mapped to components.
    const rolesQuery = useRoles(appId, undefined, { enabled: showComponentRolesModal });
    const allRoles = useMemo(() => splitRoles(rolesQuery.items).roles, [rolesQuery.items]);

    // Add/Edit Mapping
    const [newMapping, setNewMapping] = useState({ rolename: '', access: '{"access": "readonly"}' });
    const [editingRoleMappingId, setEditingRoleMappingId] = useState(null);
    const [editAccessValue, setEditAccessValue] = useState('');

    const [formData, setFormData] = useState({
        component_name: '',
        component_description: '',
    });
    const [updateFormData, setUpdateFormData] = useState({
        old_component_name: '',
        new_component_name: '',
        component_name: '',
        updated_description: '',
    });

    // URL keys: status, created_from/created_to.
    const columns = [
        {
            key: 'component_name',
            header: 'Component',
            sortKey: 'name',
            locked: true,
            render: (c) => <span className="font-medium text-slate-900">{c.component_name}</span>,
        },
        {
            key: 'component_description',
            header: 'Description',
            render: (c) =>
                c.component_description ? (
                    <div className="max-w-md truncate" title={c.component_description}>
                        {c.component_description}
                    </div>
                ) : (
                    '—'
                ),
        },
        {
            key: 'created_on',
            header: 'Created',
            sortKey: 'created',
            className: 'whitespace-nowrap',
            render: (c) => (c.created_on ? new Date(c.created_on).toLocaleDateString() : '—'),
            filter: { key: 'created', type: 'date' },
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (c) => <StatusBadge active={Boolean(c.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'component_id',
            header: 'Component ID',
            defaultVisible: false,
            className: 'font-mono text-xs text-slate-500',
            render: (c) => c.component_id || '—',
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (c) => (
                <RowActions>
                    <RowAction
                        icon={ENTITY_ICONS.roles}
                        label="View roles"
                        tone="info"
                        onClick={() => openComponentRoles(c)}
                    />
                    <RowAction icon={Edit2} label="Edit component" onClick={() => openUpdateModal(c)} />
                    <RowAction
                        icon={Trash2}
                        label="Delete component"
                        tone="danger"
                        onClick={() => openDeleteModal(c)}
                    />
                </RowActions>
            ),
        },
    ];

    const table = useServerTable((query) => componentsService.getPage(appId.trim(), query), {
        deps: [appId],
        enabled: Boolean(appId && appId.trim()),
        defaultSort: { key: 'name', dir: 'asc' },
        columns,
    });

    // A component picked under the previous app means nothing in the new one.
    useEffect(() => {
        setSelectedComponent(null);
        setShowUpdateModal(false);
        setShowDeleteModal(false);
        setShowComponentRolesModal(false);
    }, [appId]);

    const handleCreateComponent = async (e) => {
        e.preventDefault();
        if (!appId) return setError('App ID is required');
        setError('');
        try {
            await componentsService.addComponent(appId, formData);
            setShowCreateModal(false);
            resetForm();
            table.reload();
            notice.success(`Component ${formData.component_name} created.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create component');
        }
    };

    const handleUpdateName = async (e) => {
        e.preventDefault();
        if (!appId || !selectedComponent) return;
        setError('');
        try {
            await componentsService.updateName(appId, {
                old_component_name: selectedComponent.component_name,
                new_component_name: updateFormData.new_component_name,
            });
            setShowUpdateModal(false);
            setSelectedComponent(null);
            resetUpdateForm();
            table.reload();
            notice.success(`Component renamed to ${updateFormData.new_component_name}.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update name');
        }
    };

    const handleUpdateDescription = async (e) => {
        e.preventDefault();
        if (!appId || !selectedComponent) return;
        setError('');
        try {
            await componentsService.updateDescription(appId, {
                component_name: selectedComponent.component_name,
                updated_description: updateFormData.updated_description,
            });
            setShowUpdateModal(false);
            setSelectedComponent(null);
            resetUpdateForm();
            table.reload();
            notice.success(`Description of ${selectedComponent.component_name} updated.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update description');
        }
    };

    const handleDeleteComponent = async () => {
        if (!appId || !selectedComponent) return;
        const ok = await confirm({
            title: `Delete component ${selectedComponent.component_name}?`,
            body: 'Its role mappings stop granting access. This can’t be undone from the console.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setError('');
        try {
            await componentsService.removeComponent(appId, { component_name: selectedComponent.component_name });
            setShowDeleteModal(false);
            setSelectedComponent(null);
            table.reload();
            notice.success(`Component ${selectedComponent.component_name} deleted.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to delete component');
        }
    };

    const resetForm = () => {
        setFormData({ component_name: '', component_description: '' });
    };

    const resetUpdateForm = () => {
        setUpdateFormData({
            old_component_name: '',
            new_component_name: '',
            component_name: '',
            updated_description: '',
        });
    };

    const openUpdateModal = (component) => {
        setError('');
        setSelectedComponent(component);
        setUpdateFormData({
            old_component_name: component.component_name,
            new_component_name: component.component_name,
            component_name: component.component_name,
            updated_description: component.component_description || '',
        });
        setShowUpdateModal(true);
    };

    const openDeleteModal = (component) => {
        setError('');
        setSelectedComponent(component);
        setShowDeleteModal(true);
    };

    // Mapping CRUD -------------------------------------------------------------

    const openComponentRoles = async (component) => {
        if (!appId) return;
        setSelectedComponent(component);
        setComponentRoles([]);
        setLoadingComponentRoles(true);
        setShowComponentRolesModal(true);
        setMappingError('');
        setNewMapping({ rolename: '', access: '{"access": "readonly"}' });
        setEditingRoleMappingId(null);
        try {
            // Load Mappings
            const data = await componentsService.getComponentDetails(appId, component.component_name);
            const rawRoles = Array.isArray(data) && data[0]?.component_roles ? data[0].component_roles : [];
            setComponentRoles(rawRoles.filter((r) => r.role_id)); // Filter valid roles
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to load details');
        } finally {
            setLoadingComponentRoles(false);
        }
    };

    const refreshComponentRoles = async () => {
        if (!selectedComponent) return;
        try {
            const data = await componentsService.getComponentDetails(appId, selectedComponent.component_name);
            const rawRoles = Array.isArray(data) && data[0]?.component_roles ? data[0].component_roles : [];
            setComponentRoles(rawRoles.filter((r) => r.role_id)); // Filter valid roles
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Couldn’t refresh the role list.');
        }
    };

    const handleAddRoleMapping = async (e) => {
        e.preventDefault();
        if (!newMapping.rolename) return setMappingError('Select a role');
        setMappingError('');
        try {
            await componentsService.addRoleMapping(appId, {
                rolename: newMapping.rolename,
                component_name: selectedComponent.component_name,
                role_component_access: JSON.parse(newMapping.access),
            });
            setNewMapping({ ...newMapping, rolename: '' });
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to add mapping');
        }
    };

    const handleDeleteRoleMapping = async (rolename) => {
        const ok = await confirm({
            title: `Remove role ${rolename} from ${selectedComponent.component_name}?`,
            body: 'Users with this role lose access to the component. The role itself is not deleted.',
            confirmLabel: 'Remove',
            tone: 'danger',
        });
        if (!ok) return;
        setMappingError('');
        try {
            await componentsService.removeRoleMapping(appId, {
                rolename: rolename,
                component_name: selectedComponent.component_name,
            });
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to remove mapping');
        }
    };

    const startEditingRole = (role) => {
        setEditingRoleMappingId(role.role_id);
        const accessVal = typeof role.accesses === 'string' ? role.accesses : JSON.stringify(role.accesses || {});
        setEditAccessValue(accessVal);
    };

    const handleUpdateRoleMapping = async (rolename) => {
        setMappingError('');
        try {
            await componentsService.updateRoleAccess(appId, {
                rolename: rolename,
                component_name: selectedComponent.component_name,
                updated_access: JSON.parse(editAccessValue),
            });
            setEditingRoleMappingId(null);
            await refreshComponentRoles();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to update access');
        }
    };

    // Helper to filter out already mapped roles
    const availableRoles = allRoles.filter((r) => !componentRoles.some((cr) => cr.role_name === r.role_name));


    return (
        <div className="space-y-4">
            <PageHeader
                title="Components"
                actions={
                    appId && (
                        <Button
                            onClick={() => {
                                resetForm();
                                setError('');
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" /> Add Component
                        </Button>
                    )
                }
            />

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its components.</p>
                </div>
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="components"
                    rowKey="component_id"
                    searchPlaceholder="Search name, description…"
                    emptyMessage="No components found for the selected app."
                />
            )}

            {/* Create Component Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create New Component"
                size="md"
            >
                <form onSubmit={handleCreateComponent} className="space-y-4">
                    <Field label="Component Name *">
                        <TextInput
                            type="text"
                            required
                            value={formData.component_name}
                            onChange={(e) => setFormData({ ...formData, component_name: e.target.value })}
                            placeholder="Component Name"
                        />
                    </Field>
                    <Field label="Component Description *">
                        <Textarea
                            required
                            rows={4}
                            value={formData.component_description}
                            onChange={(e) => setFormData({ ...formData, component_description: e.target.value })}
                            placeholder="Component description"
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Create Component
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Update Component Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    setSelectedComponent(null);
                }}
                title={`Update ${selectedComponent?.component_name || 'Component'}`}
                size="md"
            >
                <div className="space-y-6">
                    <form onSubmit={handleUpdateName} className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-900">Update Name</h3>
                        <Field label="New Component Name *">
                            <TextInput
                                type="text"
                                required
                                value={updateFormData.new_component_name}
                                onChange={(e) =>
                                    setUpdateFormData({ ...updateFormData, new_component_name: e.target.value })
                                }
                            />
                        </Field>
                        <button
                            type="submit"
                            className="w-full rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update Name
                        </button>
                    </form>

                    <div className="border-t border-slate-200 pt-4">
                        <form onSubmit={handleUpdateDescription} className="space-y-4">
                            <h3 className="text-sm font-semibold text-slate-900">Update Description</h3>
                            <Field label="New Description *">
                                <Textarea
                                    required
                                    rows={4}
                                    value={updateFormData.updated_description}
                                    onChange={(e) =>
                                        setUpdateFormData({ ...updateFormData, updated_description: e.target.value })
                                    }
                                />
                            </Field>
                            <button
                                type="submit"
                                className="w-full rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                            >
                                Update Description
                            </button>
                        </form>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-200">
                        <button
                            onClick={() => {
                                setShowUpdateModal(false);
                                setSelectedComponent(null);
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete Component Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedComponent(null);
                }}
                title={`Delete ${selectedComponent?.component_name || 'Component'}`}
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete component <strong>{selectedComponent?.component_name}</strong>?
                        This action cannot be undone.
                    </p>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            onClick={handleDeleteComponent}
                            className="flex-1 rounded-lg bg-red-600 text-white font-medium py-2.5 hover:bg-red-700"
                        >
                            Delete
                        </button>
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedComponent(null);
                            }}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Component Roles Modal */}
            <Modal
                isOpen={showComponentRolesModal}
                onClose={() => {
                    setShowComponentRolesModal(false);
                    setSelectedComponent(null);
                    setComponentRoles([]);
                    setMappingError('');
                }}
                title={selectedComponent ? `Roles for ${selectedComponent.component_name}` : 'Roles'}
                size="lg"
            >
                <div className="space-y-6">
                    {/* Add New Mapping Form */}
                    <form
                        onSubmit={handleAddRoleMapping}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3"
                    >
                        <h4 className="text-sm font-semibold text-slate-800">Assign New Role</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={newMapping.rolename}
                                onChange={(e) => setNewMapping({ ...newMapping, rolename: e.target.value })}
                                required
                            >
                                <option value="">Select Role...</option>
                                {availableRoles.map((r) => (
                                    <option key={r.role_id} value={r.role_name}>
                                        {r.role_name}
                                    </option>
                                ))}
                            </select>
                            <input
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                                value={newMapping.access}
                                onChange={(e) => setNewMapping({ ...newMapping, access: e.target.value })}
                                placeholder="Access JSON"
                                required
                            />
                            <button
                                type="submit"
                                className="rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
                            >
                                Assign Role
                            </button>
                        </div>
                    </form>

                    {(mappingError || rolesQuery.error) && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {mappingError || rolesQuery.error}
                        </div>
                    )}

                    {loadingComponentRoles ? (
                        <SkeletonList />
                    ) : componentRoles.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                            No roles assigned to this component.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                            <table className="min-w-full text-sm text-left text-slate-700">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                    <tr>
                                        <th className="px-4 py-3">Role</th>
                                        <th className="px-4 py-3">Access</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {componentRoles.map((role) => (
                                        <tr key={role.role_id} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-3 font-semibold text-slate-900 align-top">
                                                {role.role_name || 'Unknown Role'}
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {editingRoleMappingId === role.role_id ? (
                                                    <div className="space-y-2">
                                                        <textarea
                                                            className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-mono"
                                                            rows={2}
                                                            value={editAccessValue}
                                                            onChange={(e) => setEditAccessValue(e.target.value)}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleUpdateRoleMapping(role.role_name)}
                                                                className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200 flex items-center gap-1"
                                                            >
                                                                <Save size={12} /> Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingRoleMappingId(null)}
                                                                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <code className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 block text-slate-600 font-mono break-all max-w-[300px]">
                                                        {typeof role.accesses === 'string'
                                                            ? role.accesses
                                                            : JSON.stringify(role.accesses || {})}
                                                    </code>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right align-top">
                                                <div className="flex justify-end gap-2">
                                                    {editingRoleMappingId !== role.role_id && (
                                                        <>
                                                            <Tooltip label="Edit Access">
                                                                <button
                                                                    onClick={() => startEditingRole(role)}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                >
                                                                    <Edit2 size={16} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip label="Remove Mapping">
                                                                <button
                                                                    onClick={() => handleDeleteRoleMapping(role.role_name)}
                                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ComponentsPage;
